import { enqueueSnackbar } from "notistack";
import Logger from "./logger";

// Single chokepoint for data-access health. Every one-shot read (Supabase and
// Firestore) routes through `monitored` so nothing can hang the UI silently:
// it warns if a read is slow, retries transient failures (a flaky / VPN link
// drops a request, the next one works), times out each attempt, and toasts only
// if every attempt fails. Realtime listeners (onSnapshot) can't be raced, so
// their hooks call `notifyQueryIssue` from a watchdog instead.
//
// RETRY SAFETY: only wrap idempotent reads. Writes (upserts) deliberately bypass
// this layer, so re-running `run()` can't double-apply anything.

const DEFAULT_SLOW_MS = 3000;
const DEFAULT_TIMEOUT_MS = 15000;
// Total attempts = retries + 1. 2 retries means up to 3 tries; with the linear
// backoff below a fully-dead link fails after ~3*timeout + 1s + 2s before the
// toast -- acceptable since that only hits a genuinely down connection, while
// the common case (a transient blip that rejects fast) recovers in ~1s.
const DEFAULT_RETRIES = 2;
const DEFAULT_BACKOFF_MS = 1000;

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

// Surface a data-access problem to the user (and the console) instead of leaving
// them staring at a spinner. notistack's global enqueue works outside React, so
// the data layer can call this directly.
export function notifyQueryIssue(
  message: string,
  level: "warning" | "error"
): void {
  if (level === "error") Logger.error(`[QUERY] ${message}`);
  else Logger.warn(`[QUERY] ${message}`);
  try {
    enqueueSnackbar(message, { variant: level, preventDuplicate: true });
  } catch {
    // SnackbarProvider not mounted yet (very early boot) -- the log still lands.
  }
}

// One attempt: race the read against a timeout so a stuck request can't hang.
// The losing underlying request (if we time out) is abandoned, not awaited.
function attempt<T>(
  label: string,
  run: () => Promise<T>,
  timeoutMs: number
): Promise<T> {
  let timeoutTimer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_resolve, reject) => {
    timeoutTimer = setTimeout(
      () => reject(new Error(`Query timed out: ${label}`)),
      timeoutMs
    );
  });
  return Promise.race([run(), timeout]).finally(() =>
    clearTimeout(timeoutTimer)
  ) as Promise<T>;
}

// Wrap a one-shot async read. `label` is what the user/you see in the toast and
// logs, so make it specific (e.g. "chat: find existing", "SB users_public ...").
export async function monitored<T>(
  label: string,
  run: () => Promise<T>,
  opts: {
    slowMs?: number;
    timeoutMs?: number;
    retries?: number;
    backoffMs?: number;
  } = {}
): Promise<T> {
  const slowMs = opts.slowMs ?? DEFAULT_SLOW_MS;
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const retries = opts.retries ?? DEFAULT_RETRIES;
  const backoffMs = opts.backoffMs ?? DEFAULT_BACKOFF_MS;
  const started = Date.now();

  // One slow-warning for the whole operation (across all attempts), console-only.
  const slowTimer = setTimeout(() => {
    Logger.debug(`Still loading: ${label} (>${Math.round(slowMs / 1000)}s)`);
  }, slowMs);

  try {
    let lastError: unknown;
    for (let tries = 0; tries <= retries; tries++) {
      if (tries > 0) {
        // Linear backoff: 1s, 2s, ... Give the flaky link a moment to settle.
        await delay(backoffMs * tries);
        Logger.warn(`[QUERY] ${label} retry ${tries}/${retries}`);
      }
      try {
        return await attempt(label, run, timeoutMs);
      } catch (err) {
        lastError = err;
      }
    }

    // Every attempt failed -- surface it once and propagate the last error.
    const timedOut =
      lastError instanceof Error &&
      lastError.message.startsWith("Query timed out");
    notifyQueryIssue(
      timedOut
        ? `Timed out: ${label} (>${Math.round(timeoutMs / 1000)}s).`
        : `Couldn't load: ${label}.`,
      "error"
    );
    throw lastError;
  } finally {
    clearTimeout(slowTimer);
    const ms = Date.now() - started;
    if (ms >= slowMs) Logger.warn(`[QUERY] ${label} took ${ms}ms`);
    else Logger.debug(`[QUERY] ${label} ${ms}ms`);
  }
}
